/**
 * Chargement du webchat Botpress, conditionné au consentement cookies (les
 * conversations transitent par des serveurs US). No-op sans URL de config ou
 * sans consentement. Seul l'id interne de l'utilisateur est transmis au bot.
 *
 * Le script de config est hébergé par Botpress : il appelle botpress.init()
 * avec la config publiée depuis le Studio (synchronisée à chaque republish).
 */
import { getConsent } from '@/lib/analytics'

declare global {
  interface Window {
    botpress?: {
      on: (event: string, handler: () => void) => void
      updateUser: (user: { data: Record<string, string> }) => void
    }
  }
}

const INJECT_URL = 'https://cdn.botpress.cloud/webchat/v3.6/inject.js'

let loaded = false

/**
 * Le webchat v3.6 rend le FAB (`.bpFabWrapper`) et le message proactif dans un
 * shadow DOM → le CSS global (index.css) ne les atteint pas. On injecte les
 * overrides directement dans le shadow root :
 *  - remonte la bulle au-dessus de la nav mobile (items h-14 = 56px + safe-area,
 *    cf. AppLayout) au lieu du `bottom:24px` par défaut qui la recouvre ;
 *  - repasse bulle + message proactif sous les modales/sheets de l'app.
 * La fenêtre de chat ouverte (`.bpWebchat`, élément séparé) garde sa priorité.
 */
const FAB_OVERRIDES = `
.bpFabWrapper, .bpMessagePreview { z-index: 30 !important; }
@media (max-width: 767px) {
  .bpFabWrapper {
    bottom: calc(3.5rem + env(safe-area-inset-bottom) + 1rem) !important;
    right: 12px !important;
  }
  .bpFab { width: 48px !important; height: 48px !important; }
}
`

// L'app n'utilise pas de shadow DOM ailleurs → le seul shadow root de la page
// est celui du webchat Botpress.
function findBotpressShadow(): ShadowRoot | null {
  const host = [...document.querySelectorAll('*')].find((el) => el.shadowRoot)
  return host?.shadowRoot ?? null
}

function injectInto(root: ShadowRoot): void {
  if (root.getElementById('ai-fab-overrides')) return
  const style = document.createElement('style')
  style.id = 'ai-fab-overrides'
  style.textContent = FAB_OVERRIDES
  root.appendChild(style)
}

/**
 * Injecte les overrides dès l'APPARITION du shadow root, pas à `webchat:ready`.
 * Le callback d'un MutationObserver s'exécute en microtâche, AVANT le paint :
 * le style est posé avant le premier rendu du FAB → pas de saut visible
 * (taille/position) au chargement. Un `setTimeout` (macrotâche, post-paint)
 * provoquait ce saut.
 */
function watchAndStyleFab(): void {
  const root = findBotpressShadow()
  if (root) {
    injectInto(root)
    return
  }
  const observer = new MutationObserver(() => {
    const r = findBotpressShadow()
    if (r) {
      injectInto(r)
      observer.disconnect()
    }
  })
  observer.observe(document.documentElement, { childList: true, subtree: true })
  // Filet de sécurité : ne pas observer indéfiniment si le webchat ne monte pas.
  setTimeout(() => observer.disconnect(), 30_000)
}

function appendScript(src: string, onload?: () => void): void {
  const script = document.createElement('script')
  script.src = src
  script.async = true
  if (onload) script.onload = onload
  document.body.appendChild(script)
}

export function initBotpress(userId?: string): void {
  const configUrl = import.meta.env.VITE_BOTPRESS_CONFIG_URL as string | undefined
  if (loaded || !configUrl || getConsent() !== 'accepted') return
  loaded = true

  // Armé avant le montage du webchat pour capter le shadow root dès sa création.
  watchAndStyleFab()

  appendScript(INJECT_URL, () => {
    appendScript(configUrl, () => {
      window.botpress?.on('webchat:ready', () => {
        const root = findBotpressShadow()
        if (root) injectInto(root) // filet : au cas où l'observer aurait manqué
        if (userId) window.botpress?.updateUser({ data: { appUserId: userId } })
      })
    })
  })
}
